/**
 * Checklist API Client
 * SmartNyuki-style checklist management
 */

import { apiClient } from './index';

export interface CategoryNode {
  id: number;
  name: string;
  slug?: string;
  description?: string;
  type: string;
  icon?: string;
  color?: string;
  order?: number;
  input_type?: string;
  input_options?: any[];
  is_required?: boolean;
  parent_id?: number;
  children: CategoryNode[];
  state?: {
    selected: boolean;
    expanded: boolean;
  };
}

export interface Checklist {
  id: number;
  name: string;
  description?: string;
  category_ids?: number[];
  taxonomy?: CategoryNode[];
  owner: boolean;
  researches?: string[];
  created_at: string;
  updated_at: string;
}

export interface ChecklistListResponse {
  success: boolean;
  data: {
    checklists: Checklist[];
    checklist: Checklist | null;
  };
  message: string;
}

export interface ChecklistResponse {
  success: boolean;
  data: Checklist;
  message: string;
}

export interface CategoryTreeResponse {
  success: boolean;
  data: CategoryNode[];
  message: string;
}

/**
 * Get all checklists for current user
 */
export const getChecklists = async (): Promise<ChecklistListResponse> => {
  const response = await apiClient.get('/checklists/');
  const result = response.data;

  // Extract category_ids from default checklist if it has taxonomy
  if (result.data.checklist?.taxonomy) {
    if (!result.data.checklist.category_ids || result.data.checklist.category_ids.length === 0) {
      result.data.checklist.category_ids = extractSelectedIds(result.data.checklist.taxonomy);
    }
  }

  return result;
};

/**
 * Extract selected category IDs from taxonomy tree
 */
const extractSelectedIds = (nodes: CategoryNode[]): number[] => {
  const ids: number[] = [];

  const traverse = (node: CategoryNode) => {
    if (node.state?.selected) {
      ids.push(node.id);
    }
    if (node.children && node.children.length > 0) {
      node.children.forEach(traverse);
    }
  };

  nodes.forEach(traverse);
  return ids;
};

/**
 * Get single checklist with full taxonomy
 */
export const getChecklist = async (id: number): Promise<ChecklistResponse> => {
  const response = await apiClient.get(`/checklists/${id}/`);
  const checklist = response.data.data;

  // Extract category_ids from taxonomy if not already present
  if (checklist.taxonomy && (!checklist.category_ids || checklist.category_ids.length === 0)) {
    checklist.category_ids = extractSelectedIds(checklist.taxonomy);
  }

  return response.data;
};

/**
 * Create new checklist
 */
export const createChecklist = async (data: {
  name: string;
  description?: string;
  category_ids?: number[];
}): Promise<ChecklistResponse> => {
  const response = await apiClient.post('/checklists/', data);
  const result = response.data;

  if (result?.data?.checklist) {
    return {
      ...result,
      data: result.data.checklist,
    };
  }

  return result;
};

/**
 * Update checklist
 */
export const updateChecklist = async (
  id: number,
  data: {
    name?: string;
    description?: string;
    category_ids?: number[];
  }
): Promise<ChecklistResponse> => {
  const response = await apiClient.put(`/checklists/${id}/`, data);
  return response.data;
};

/**
 * Delete checklist
 */
export const deleteChecklist = async (id: number): Promise<void> => {
  await apiClient.delete(`/checklists/${id}/`);
};

/**
 * Get category tree for checklists
 */
export const getCategoryTree = async (): Promise<CategoryTreeResponse> => {
  const response = await apiClient.get('/checklists/categories/');
  return response.data;
};

/**
 * Duplicate checklist
 */
export const duplicateChecklist = async (id: number): Promise<ChecklistResponse> => {
  const checklist = await getChecklist(id);
  const duplicated = {
    name: `${checklist.data.name} (copy)`,
    description: checklist.data.description,
    category_ids: checklist.data.category_ids,
  };
  return createChecklist(duplicated);
};

/**
 * API object for Redux slice compatibility
 */
export const checklistsApi = {
  getChecklists,
  getChecklist,
  createChecklist,
  updateChecklist,
  deleteChecklist,
  getCategoryTree,
  duplicateChecklist,
};
